import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useState, type ReactNode } from 'react';
import type { MemberSelectorListItem, PermissionGroupMemberListItem, PermissionGroupSummary } from '../appService';
import type { PermissionKey } from '../model';
import { PERMISSION_DEFINITIONS, formatPermissionKeyLabels } from '../permissions';
import type { WorkspaceBootstrapContext } from './appTypes';
import { useAppRuntime, useWorkspaceBootstrapContext, useWorkspaceContext, useWorkspaceRouteAdapter } from './context';
import {
  invalidatePermissionGroupMemberMutationQueries,
  invalidatePermissionGroupStructureQueries,
  useMemberSelectorItemsQuery,
  usePermissionGroupDetailQuery,
  usePermissionGroupListQuery
} from './data';
import {
  ActionError,
  BackHeader,
  CompactEmptyRow,
  ConfirmedField,
  ModuleEmptyState,
  PendingButton,
  RecoverableReadError,
  SectionLoading,
  SvgIcon,
  TextField,
  formatActionError,
  formatMemberGroupNames
} from './ui';

export function PermissionGroupsPage(): ReactNode {
  const appContext = useWorkspaceBootstrapContext();
  const routeAdapter = useWorkspaceRouteAdapter();
  const canManage = canManagePermissionGroups(appContext);
  const permissionGroups = usePermissionGroupListQuery(canManage);

  if (!canManage) {
    return (
      <PermissionGroupPermissionDenied
        title="权限组"
        action="back-to-member-settings"
        label="返回成员设置"
        onBack={routeAdapter.backToMemberSettings}
      />
    );
  }

  return (
    <div className="route-outlet">
      <BackHeader
        title="权限组"
        action="back-to-member-settings"
        label="返回成员设置"
        onBack={routeAdapter.backToMemberSettings}
      />
      <section className="schedule-detail-section">
        <div className="section-heading-row">
          <h2>当前权限组</h2>
          <button
            type="button"
            className="text-button"
            data-action="open-create-permission-group"
            onClick={routeAdapter.openCreatePermissionGroup}
          >
            新建
          </button>
        </div>
      </section>
      <PermissionGroupSummaryListContent permissionGroups={permissionGroups} />
    </div>
  );
}

export function CreatePermissionGroupPage(): ReactNode {
  const appContext = useWorkspaceBootstrapContext();
  const { service } = useAppRuntime();
  const workspaceContext = useWorkspaceContext();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const canManage = canManagePermissionGroups(appContext);
  const createPermissionGroup = useMutation({
    mutationFn: (input: { readonly name: string; readonly permissionKeys: readonly PermissionKey[] }) =>
      service.createPermissionGroup({
        workspaceContext,
        name: input.name,
        permissionKeys: input.permissionKeys
      }),
    onSuccess: async (createdPermissionGroup) => {
      await invalidatePermissionGroupStructureQueries(
        queryClient,
        workspaceContext,
        createdPermissionGroup.permissionGroupId
      );
      void navigate({
        to: '/workspace/permission-groups/$permissionGroupId',
        params: { permissionGroupId: createdPermissionGroup.permissionGroupId },
        replace: true,
        resetScroll: true
      });
    }
  });

  if (!canManage) {
    return (
      <PermissionGroupPermissionDenied
        title="新建权限组"
        action="back-to-permission-groups"
        label="返回权限组"
        onBack={() => {
          void navigate({ to: '/workspace/permission-groups', replace: true, resetScroll: true });
        }}
      />
    );
  }

  return (
    <PermissionGroupFormRoute
      title="新建权限组"
      initialName=""
      initialPermissionKeys={[]}
      savePending={createPermissionGroup.isPending}
      onBack={() => {
        void navigate({ to: '/workspace/permission-groups', replace: true, resetScroll: true });
      }}
      onSubmit={(input) => createPermissionGroup.mutateAsync(input)}
    />
  );
}

export function PermissionGroupDetailPage({ permissionGroupId }: { readonly permissionGroupId: string }): ReactNode {
  const appContext = useWorkspaceBootstrapContext();
  const navigate = useNavigate();
  const canManage = canManagePermissionGroups(appContext);
  const detail = usePermissionGroupDetailQuery(permissionGroupId, canManage);

  if (!canManage) {
    return (
      <PermissionGroupPermissionDenied
        title="权限组详情"
        action="back-to-permission-groups"
        label="返回权限组"
        onBack={() => {
          void navigate({ to: '/workspace/permission-groups', replace: true, resetScroll: true });
        }}
      />
    );
  }

  return (
    <div className="route-outlet">
      <PermissionGroupBackHeader title="权限组详情" pending={false} />
      <PermissionGroupDetailContent detail={detail} />
    </div>
  );
}

export function EditPermissionGroupPage({ permissionGroupId }: { readonly permissionGroupId: string }): ReactNode {
  const appContext = useWorkspaceBootstrapContext();
  const { service } = useAppRuntime();
  const workspaceContext = useWorkspaceContext();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const canManage = canManagePermissionGroups(appContext);
  const detail = usePermissionGroupDetailQuery(permissionGroupId, canManage);
  const updatePermissionGroup = useMutation({
    mutationFn: (input: { readonly name: string; readonly permissionKeys: readonly PermissionKey[] }) =>
      service.updatePermissionGroup({
        workspaceContext,
        permissionGroupId,
        name: input.name,
        permissionKeys: input.permissionKeys
      }),
    onSuccess: async () => {
      await invalidatePermissionGroupStructureQueries(queryClient, workspaceContext, permissionGroupId);
      void navigate({
        to: '/workspace/permission-groups/$permissionGroupId',
        params: { permissionGroupId },
        replace: true,
        resetScroll: true
      });
    }
  });

  if (!canManage) {
    return (
      <PermissionGroupPermissionDenied
        title="编辑权限组"
        action="back-to-permission-group-detail"
        label="返回权限组详情"
        onBack={() => {
          void navigate({
            to: '/workspace/permission-groups/$permissionGroupId',
            params: { permissionGroupId },
            replace: true,
            resetScroll: true
          });
        }}
      />
    );
  }

  if (detail.status === 'pending') {
    return (
      <div className="route-outlet">
        <PermissionGroupDetailBackHeader
          title="编辑权限组"
          permissionGroupId={permissionGroupId}
          pending={updatePermissionGroup.isPending}
        />
        <SectionLoading body="正在读取权限组详情。" />
      </div>
    );
  }

  if (detail.status === 'error') {
    return (
      <div className="route-outlet">
        <PermissionGroupDetailBackHeader
          title="编辑权限组"
          permissionGroupId={permissionGroupId}
          pending={updatePermissionGroup.isPending}
        />
        <RecoverableReadError
          error={detail.error}
          retryAction="retry-edit-permission-group"
          onRetry={() => void detail.refetch()}
        />
      </div>
    );
  }

  return (
    <PermissionGroupFormRoute
      key={detail.data.permissionGroupId}
      title="编辑权限组"
      initialName={detail.data.name}
      initialPermissionKeys={detail.data.permissionKeys}
      savePending={updatePermissionGroup.isPending}
      onBack={() => {
        void navigate({
          to: '/workspace/permission-groups/$permissionGroupId',
          params: { permissionGroupId },
          replace: true,
          resetScroll: true
        });
      }}
      onSubmit={(input) => updatePermissionGroup.mutateAsync(input)}
    />
  );
}

export function DeletePermissionGroupPage({ permissionGroupId }: { readonly permissionGroupId: string }): ReactNode {
  const appContext = useWorkspaceBootstrapContext();
  const { service } = useAppRuntime();
  const workspaceContext = useWorkspaceContext();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const canManage = canManagePermissionGroups(appContext);
  const detail = usePermissionGroupDetailQuery(permissionGroupId, canManage);
  const [actionError, setActionError] = useState<string | null>(null);
  const deletePermissionGroup = useMutation({
    mutationFn: () => service.deletePermissionGroup({ workspaceContext, permissionGroupId }),
    onSuccess: async () => {
      await invalidatePermissionGroupStructureQueries(queryClient, workspaceContext, permissionGroupId);
      void navigate({ to: '/workspace/permission-groups', replace: true, resetScroll: true });
    }
  });

  if (!canManage) {
    return (
      <PermissionGroupPermissionDenied
        title="删除权限组"
        action="back-to-permission-group-detail"
        label="返回权限组详情"
        onBack={() => {
          void navigate({
            to: '/workspace/permission-groups/$permissionGroupId',
            params: { permissionGroupId },
            replace: true,
            resetScroll: true
          });
        }}
      />
    );
  }

  if (detail.status === 'pending') {
    return (
      <div className="route-outlet">
        <PermissionGroupDetailBackHeader
          title="删除权限组"
          permissionGroupId={permissionGroupId}
          pending={deletePermissionGroup.isPending}
        />
        <SectionLoading body="正在读取权限组详情。" />
      </div>
    );
  }

  if (detail.status === 'error') {
    return (
      <div className="route-outlet">
        <PermissionGroupDetailBackHeader
          title="删除权限组"
          permissionGroupId={permissionGroupId}
          pending={deletePermissionGroup.isPending}
        />
        <RecoverableReadError
          error={detail.error}
          retryAction="retry-delete-permission-group"
          onRetry={() => void detail.refetch()}
        />
      </div>
    );
  }

  return (
    <div className="route-outlet">
      <PermissionGroupDetailBackHeader
        title="删除权限组"
        permissionGroupId={permissionGroupId}
        pending={deletePermissionGroup.isPending}
      />
      <section className="confirm-card">
        <h2>{`删除“${detail.data.name}”？`}</h2>
        <p>删除后，这个权限组的权限项和成员授权会同时移除。</p>
        <ActionError message={actionError} />
        <div className="footer-actions">
          <button
            type="button"
            className="secondary-button"
            data-action="cancel-delete-permission-group"
            disabled={deletePermissionGroup.isPending}
            onClick={() => {
              void navigate({
                to: '/workspace/permission-groups/$permissionGroupId',
                params: { permissionGroupId },
                replace: true,
                resetScroll: true
              });
            }}
          >
            取消
          </button>
          <PendingButton
            action="confirm-delete-permission-group"
            label="确认删除"
            variant="secondary"
            pending={deletePermissionGroup.isPending}
            onClick={() => {
              void (async () => {
                setActionError(null);
                try {
                  await deletePermissionGroup.mutateAsync();
                } catch (error) {
                  setActionError(formatActionError(error));
                }
              })();
            }}
          />
        </div>
      </section>
    </div>
  );
}

export function ManagePermissionGroupMembersPage({
  permissionGroupId
}: {
  readonly permissionGroupId: string;
}): ReactNode {
  const appContext = useWorkspaceBootstrapContext();
  const { service } = useAppRuntime();
  const workspaceContext = useWorkspaceContext();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const canManage = canManagePermissionGroups(appContext);
  const detail = usePermissionGroupDetailQuery(permissionGroupId, canManage);
  const selector = useMemberSelectorItemsQuery('member_selection', false);
  const [isPickerOpen, setPickerOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const addMember = useMutation({
    mutationFn: (seasonMembershipId: string) =>
      service.addPermissionGroupMember({ workspaceContext, permissionGroupId, seasonMembershipId }),
    onSuccess: async () => {
      await invalidatePermissionGroupMemberMutationQueries(queryClient, workspaceContext, permissionGroupId);
      setPickerOpen(false);
    }
  });

  if (!canManage) {
    return (
      <PermissionGroupPermissionDenied
        title="管理权限组成员"
        action="back-to-permission-group-detail"
        label="返回权限组详情"
        onBack={() => {
          void navigate({
            to: '/workspace/permission-groups/$permissionGroupId',
            params: { permissionGroupId },
            replace: true,
            resetScroll: true
          });
        }}
      />
    );
  }

  return (
    <div className="route-outlet">
      <PermissionGroupMembersBackHeader
        title="管理成员"
        permissionGroupId={permissionGroupId}
        pending={addMember.isPending}
        action="back-to-permission-group-detail"
        label="返回权限组详情"
        onBack={() => {
          void navigate({
            to: '/workspace/permission-groups/$permissionGroupId',
            params: { permissionGroupId },
            replace: true,
            resetScroll: true
          });
        }}
      />
      <PermissionGroupMembersManageContent
        detail={detail}
        selector={selector}
        isPickerOpen={isPickerOpen}
        pendingSeasonMembershipId={addMember.isPending ? addMember.variables : null}
        actionError={actionError}
        onRetry={() => void detail.refetch()}
        onOpenRemove={(seasonMembershipId) => {
          void navigate({
            to: '/workspace/permission-groups/$permissionGroupId/members/$seasonMembershipId/remove',
            params: { permissionGroupId, seasonMembershipId },
            resetScroll: true
          });
        }}
        onTogglePicker={() => {
          setActionError(null);
          setPickerOpen(true);
        }}
        onAddMember={(seasonMembershipId) => {
          void (async () => {
            setActionError(null);
            try {
              await addMember.mutateAsync(seasonMembershipId);
            } catch (error) {
              setActionError(formatActionError(error));
            }
          })();
        }}
      />
    </div>
  );
}

export function RemovePermissionGroupMemberPage({
  permissionGroupId,
  seasonMembershipId
}: {
  readonly permissionGroupId: string;
  readonly seasonMembershipId: string;
}): ReactNode {
  const appContext = useWorkspaceBootstrapContext();
  const { service } = useAppRuntime();
  const workspaceContext = useWorkspaceContext();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const canManage = canManagePermissionGroups(appContext);
  const detail = usePermissionGroupDetailQuery(permissionGroupId, canManage);
  const [actionError, setActionError] = useState<string | null>(null);
  const removeMember = useMutation({
    mutationFn: () => service.removePermissionGroupMember({ workspaceContext, permissionGroupId, seasonMembershipId }),
    onSuccess: async () => {
      await invalidatePermissionGroupMemberMutationQueries(queryClient, workspaceContext, permissionGroupId);
      void navigate({
        to: '/workspace/permission-groups/$permissionGroupId/members',
        params: { permissionGroupId },
        replace: true,
        resetScroll: true
      });
    }
  });

  if (!canManage) {
    return (
      <PermissionGroupPermissionDenied
        title="确认移出成员"
        action="back-to-manage-permission-group-members"
        label="返回管理成员"
        onBack={() => {
          void navigate({
            to: '/workspace/permission-groups/$permissionGroupId/members',
            params: { permissionGroupId },
            replace: true,
            resetScroll: true
          });
        }}
      />
    );
  }

  if (detail.status === 'pending') {
    return (
      <div className="route-outlet">
        <PermissionGroupMembersBackHeader
          title="确认移出成员"
          permissionGroupId={permissionGroupId}
          pending={removeMember.isPending}
        />
        <SectionLoading body="正在读取权限组详情。" />
      </div>
    );
  }

  if (detail.status === 'error') {
    return (
      <div className="route-outlet">
        <PermissionGroupMembersBackHeader
          title="确认移出成员"
          permissionGroupId={permissionGroupId}
          pending={removeMember.isPending}
        />
        <RecoverableReadError
          error={detail.error}
          retryAction="retry-confirm-remove-permission-group-member"
          onRetry={() => void detail.refetch()}
        />
      </div>
    );
  }

  const member = detail.data.members.find((candidate) => candidate.seasonMembershipId === seasonMembershipId);

  return (
    <div className="route-outlet">
      <PermissionGroupMembersBackHeader
        title="确认移出成员"
        permissionGroupId={permissionGroupId}
        pending={removeMember.isPending}
      />
      {!member ? (
        <section className="panel error" role="alert" tabIndex={-1}>
          <p>找不到这个权限组成员。</p>
        </section>
      ) : (
        <section className="confirm-card">
          <h2>{`移出${member.nickname}？`}</h2>
          <p>{`移出后，${member.nickname}会失去这个权限组授予的权限。`}</p>
          <ActionError message={actionError} />
          <div className="footer-actions">
            <button
              type="button"
              className="secondary-button"
              data-action="cancel-remove-permission-group-member"
              disabled={removeMember.isPending}
              onClick={() => {
                void navigate({
                  to: '/workspace/permission-groups/$permissionGroupId/members',
                  params: { permissionGroupId },
                  replace: true,
                  resetScroll: true
                });
              }}
            >
              取消
            </button>
            <PendingButton
              action="confirm-remove-permission-group-member"
              label="确认移出"
              variant="secondary"
              pending={removeMember.isPending}
              onClick={() => {
                void (async () => {
                  setActionError(null);
                  try {
                    await removeMember.mutateAsync();
                  } catch (error) {
                    setActionError(formatActionError(error));
                  }
                })();
              }}
            />
          </div>
        </section>
      )}
    </div>
  );
}

function PermissionGroupSummaryListContent({
  permissionGroups
}: {
  readonly permissionGroups: ReturnType<typeof usePermissionGroupListQuery>;
}): ReactNode {
  if (permissionGroups.status === 'pending') {
    return <SectionLoading body="正在读取权限组。" />;
  }

  if (permissionGroups.status === 'error') {
    return (
      <RecoverableReadError
        error={permissionGroups.error}
        retryAction="retry-permission-groups"
        onRetry={() => void permissionGroups.refetch()}
      />
    );
  }

  if (permissionGroups.data.length === 0) {
    return <ModuleEmptyState icon="shield" body="暂无权限组" />;
  }

  return <PermissionGroupSummaryRows permissionGroups={permissionGroups.data} />;
}

function PermissionGroupSummaryRows({
  permissionGroups
}: {
  readonly permissionGroups: readonly PermissionGroupSummary[];
}): ReactNode {
  const navigate = useNavigate();

  return (
    <section className="permission-group-list prototype-list" role="list">
      {permissionGroups.map((permissionGroup) => (
        <article
          className="permission-group-row prototype-list-row"
          role="listitem"
          data-permission-group-id={permissionGroup.permissionGroupId}
          key={permissionGroup.permissionGroupId}
        >
          <span className="member-avatar member-avatar-icon" aria-hidden="true">
            <SvgIcon name="shield" className="member-avatar-svg" />
          </span>
          <span className="member-copy">
            <strong>{permissionGroup.name}</strong>
            <small>{formatPermissionGroupSummaryMeta(permissionGroup)}</small>
          </span>
          <span className="member-group-row-actions">
            <button
              type="button"
              className="text-button"
              data-action="open-permission-group-detail"
              data-permission-group-id={permissionGroup.permissionGroupId}
              onClick={() => {
                void navigate({
                  to: '/workspace/permission-groups/$permissionGroupId',
                  params: { permissionGroupId: permissionGroup.permissionGroupId },
                  resetScroll: true
                });
              }}
            >
              详情
            </button>
            <button
              type="button"
              className="text-button"
              data-action="edit-permission-group"
              data-permission-group-id={permissionGroup.permissionGroupId}
              onClick={() => {
                void navigate({
                  to: '/workspace/permission-groups/$permissionGroupId/edit',
                  params: { permissionGroupId: permissionGroup.permissionGroupId },
                  resetScroll: true
                });
              }}
            >
              编辑
            </button>
          </span>
        </article>
      ))}
    </section>
  );
}

function PermissionGroupDetailContent({
  detail
}: {
  readonly detail: ReturnType<typeof usePermissionGroupDetailQuery>;
}): ReactNode {
  const navigate = useNavigate();

  if (detail.status === 'pending') {
    return <SectionLoading body="正在读取权限组详情。" />;
  }

  if (detail.status === 'error') {
    return (
      <RecoverableReadError
        error={detail.error}
        retryAction="retry-permission-group-detail"
        onRetry={() => void detail.refetch()}
      />
    );
  }

  const permissionGroup = detail.data;

  return (
    <>
      <section className="schedule-detail-section">
        <div className="section-heading-row">
          <h2>{permissionGroup.name}</h2>
          <span className="member-group-row-actions">
            <button
              type="button"
              className="text-button"
              data-action="edit-permission-group"
              onClick={() => {
                void navigate({
                  to: '/workspace/permission-groups/$permissionGroupId/edit',
                  params: { permissionGroupId: permissionGroup.permissionGroupId },
                  resetScroll: true
                });
              }}
            >
              编辑
            </button>
            <button
              type="button"
              className="text-button danger-text-button"
              data-action="delete-permission-group"
              onClick={() => {
                void navigate({
                  to: '/workspace/permission-groups/$permissionGroupId/delete',
                  params: { permissionGroupId: permissionGroup.permissionGroupId },
                  resetScroll: true
                });
              }}
            >
              删除
            </button>
          </span>
        </div>
        <ConfirmedField label="权限" value={formatPermissionKeys(permissionGroup.permissionKeys)} />
        <ConfirmedField label="成员" value={`${permissionGroup.members.length} 人`} />
      </section>
      <PermissionGroupMembersStatus members={permissionGroup.members} />
      <div className="footer-actions">
        <button
          type="button"
          className="secondary-button"
          data-action="open-manage-permission-group-members"
          onClick={() => {
            void navigate({
              to: '/workspace/permission-groups/$permissionGroupId/members',
              params: { permissionGroupId: permissionGroup.permissionGroupId },
              resetScroll: true
            });
          }}
        >
          管理成员
        </button>
      </div>
    </>
  );
}

function PermissionGroupFormRoute({
  title,
  initialName,
  initialPermissionKeys,
  savePending,
  onBack,
  onSubmit
}: {
  readonly title: string;
  readonly initialName: string;
  readonly initialPermissionKeys: readonly PermissionKey[];
  readonly savePending: boolean;
  readonly onBack: () => void;
  readonly onSubmit: (input: {
    readonly name: string;
    readonly permissionKeys: readonly PermissionKey[];
  }) => Promise<unknown>;
}): ReactNode {
  const [name, setName] = useState(initialName);
  const [selectedPermissionKeys, setSelectedPermissionKeys] = useState<readonly PermissionKey[]>(initialPermissionKeys);
  const [actionError, setActionError] = useState<string | null>(null);

  return (
    <div className="route-outlet">
      <BackHeader
        title={title}
        action="back-to-permission-groups"
        label="返回权限组"
        pending={savePending}
        onBack={onBack}
      />
      <section className="member-group-form">
        <TextField
          label="权限组名称"
          field="permission-group-name"
          value={name}
          onChange={(nextName) => {
            setActionError(null);
            setName(nextName);
          }}
        />
      </section>
      <section className="schedule-detail-section">
        <div className="section-heading-row">
          <h2>权限项</h2>
        </div>
        <div className="approval-member-group-choices">
          {PERMISSION_DEFINITIONS.map((permission) => (
            <label className="approval-member-group-choice" key={permission.key}>
              <input
                type="checkbox"
                data-permission-key={permission.key}
                checked={selectedPermissionKeys.includes(permission.key)}
                disabled={savePending}
                onChange={() => {
                  setActionError(null);
                  setSelectedPermissionKeys((current) => togglePermissionKey(current, permission.key));
                }}
              />
              <span>{permission.label}</span>
              <small>{permission.description}</small>
            </label>
          ))}
        </div>
      </section>
      <ActionError message={actionError} />
      <div className="footer-actions">
        <PendingButton
          action="save-permission-group"
          label="保存"
          variant="primary"
          pending={savePending}
          onClick={() => {
            void (async () => {
              setActionError(null);
              try {
                await onSubmit({ name, permissionKeys: selectedPermissionKeys });
              } catch (error) {
                setActionError(formatActionError(error));
              }
            })();
          }}
        />
      </div>
    </div>
  );
}

function PermissionGroupMembersManageContent({
  detail,
  selector,
  isPickerOpen,
  pendingSeasonMembershipId,
  actionError,
  onRetry,
  onOpenRemove,
  onTogglePicker,
  onAddMember
}: {
  readonly detail: ReturnType<typeof usePermissionGroupDetailQuery>;
  readonly selector: ReturnType<typeof useMemberSelectorItemsQuery>;
  readonly isPickerOpen: boolean;
  readonly pendingSeasonMembershipId: string | null | undefined;
  readonly actionError: string | null;
  readonly onRetry: () => void;
  readonly onOpenRemove: (seasonMembershipId: string) => void;
  readonly onTogglePicker: () => void;
  readonly onAddMember: (seasonMembershipId: string) => void;
}): ReactNode {
  if (detail.status === 'pending') {
    return <SectionLoading body="正在读取权限组详情。" />;
  }

  if (detail.status === 'error') {
    return (
      <RecoverableReadError
        error={detail.error}
        retryAction="retry-manage-permission-group-members"
        onRetry={onRetry}
      />
    );
  }

  const currentMemberIds = new Set(detail.data.members.map((member) => member.seasonMembershipId));

  return (
    <>
      <p className="subtle-page-subtitle">添加成员后，权限会立即按当前权限项生效；移出成员前会二次确认。</p>
      <PermissionGroupMembersManageRows
        members={detail.data.members}
        pendingSeasonMembershipId={pendingSeasonMembershipId}
        onOpenRemove={onOpenRemove}
      />
      <ActionError message={actionError} />
      <div className="footer-actions">
        <button
          type="button"
          className="secondary-button"
          data-action="add-permission-group-member"
          onClick={onTogglePicker}
        >
          添加成员
        </button>
      </div>
      {isPickerOpen ? (
        <InlinePermissionGroupMemberPicker
          members={selector}
          excludedSeasonMembershipIds={currentMemberIds}
          pendingSeasonMembershipId={pendingSeasonMembershipId}
          onSelect={onAddMember}
        />
      ) : null}
    </>
  );
}

function PermissionGroupMembersStatus({
  members
}: {
  readonly members: readonly PermissionGroupMemberListItem[];
}): ReactNode {
  if (members.length === 0) {
    return <CompactEmptyRow body="暂无权限组成员" />;
  }

  return (
    <section className="permission-group-member-list prototype-list" role="list">
      {members.map((member) => (
        <article
          className="permission-group-member-row prototype-list-row"
          role="listitem"
          key={member.seasonMembershipId}
        >
          <span className="member-copy">
            <strong>{member.nickname}</strong>
            <small>{formatMemberGroupNames(member.memberGroups)}</small>
          </span>
        </article>
      ))}
    </section>
  );
}

function PermissionGroupMembersManageRows({
  members,
  pendingSeasonMembershipId,
  onOpenRemove
}: {
  readonly members: readonly PermissionGroupMemberListItem[];
  readonly pendingSeasonMembershipId: string | null | undefined;
  readonly onOpenRemove: (seasonMembershipId: string) => void;
}): ReactNode {
  if (members.length === 0) {
    return <CompactEmptyRow body="当前权限组还没有成员。" />;
  }

  return (
    <section className="permission-group-member-list prototype-list" role="list">
      {members.map((member) => (
        <article className="prototype-list-row manage-row" role="listitem" key={member.seasonMembershipId}>
          <span className="manage-row-main">
            <span className="member-avatar" aria-hidden="true">
              {member.nickname.trim().slice(0, 1) || '成'}
            </span>
            <span className="member-copy">
              <strong>{member.nickname}</strong>
              <small>{formatMemberGroupNames(member.memberGroups)}</small>
            </span>
          </span>
          <button
            type="button"
            className="delete-icon"
            data-action="open-remove-permission-group-member-confirm"
            data-season-membership-id={member.seasonMembershipId}
            aria-label={`从权限组移出${member.nickname}`}
            disabled={pendingSeasonMembershipId === member.seasonMembershipId}
            onClick={() => onOpenRemove(member.seasonMembershipId)}
          >
            <SvgIcon name="trash" className="delete-icon-svg" />
          </button>
        </article>
      ))}
    </section>
  );
}

function InlinePermissionGroupMemberPicker({
  members,
  excludedSeasonMembershipIds,
  pendingSeasonMembershipId,
  onSelect
}: {
  readonly members: ReturnType<typeof useMemberSelectorItemsQuery>;
  readonly excludedSeasonMembershipIds: ReadonlySet<string>;
  readonly pendingSeasonMembershipId: string | null | undefined;
  readonly onSelect: (seasonMembershipId: string) => void;
}): ReactNode {
  useEffect(() => {
    if (members.fetchStatus === 'idle' && members.status === 'pending') {
      void members.refetch();
    }
  }, [members.fetchStatus, members.refetch, members.status]);

  if (members.status === 'pending') {
    return <SectionLoading body="正在读取成员选择列表。" />;
  }

  if (members.status === 'error') {
    return (
      <RecoverableReadError
        error={members.error}
        retryAction="retry-permission-group-member-selector"
        onRetry={() => void members.refetch()}
      />
    );
  }

  const eligibleMembers = members.data.filter((member) => !excludedSeasonMembershipIds.has(member.seasonMembershipId));

  if (eligibleMembers.length === 0) {
    return (
      <section className="inline-member-picker prototype-list">
        <CompactEmptyRow body="当前乐季没有可添加成员。" />
      </section>
    );
  }

  return (
    <section className="inline-member-picker prototype-list" role="list">
      {eligibleMembers.map((member) => (
        <button
          type="button"
          className="member-row prototype-list-row"
          data-action="select-permission-group-member"
          data-season-membership-id={member.seasonMembershipId}
          disabled={pendingSeasonMembershipId === member.seasonMembershipId}
          aria-busy={pendingSeasonMembershipId === member.seasonMembershipId ? 'true' : undefined}
          role="listitem"
          key={member.seasonMembershipId}
          onClick={() => onSelect(member.seasonMembershipId)}
        >
          <span className="member-avatar" aria-hidden="true">
            {member.nickname.trim().slice(0, 1) || '成'}
          </span>
          <span className="member-copy">
            <strong>{member.nickname}</strong>
            <small>{formatSelectorMemberGroups(member)}</small>
          </span>
        </button>
      ))}
    </section>
  );
}

function PermissionGroupBackHeader({
  title,
  pending
}: {
  readonly title: string;
  readonly pending: boolean;
}): ReactNode {
  const routeAdapter = useWorkspaceRouteAdapter();
  return (
    <BackHeader
      title={title}
      action="back-to-permission-groups"
      label="返回权限组"
      pending={pending}
      onBack={() => {
        routeAdapter.openPermissionGroups();
      }}
    />
  );
}

function PermissionGroupDetailBackHeader({
  title,
  permissionGroupId,
  pending
}: {
  readonly title: string;
  readonly permissionGroupId: string;
  readonly pending: boolean;
}): ReactNode {
  const navigate = useNavigate();
  return (
    <BackHeader
      title={title}
      action="back-to-permission-group-detail"
      label="返回权限组详情"
      pending={pending}
      onBack={() => {
        void navigate({
          to: '/workspace/permission-groups/$permissionGroupId',
          params: { permissionGroupId },
          replace: true,
          resetScroll: true
        });
      }}
    />
  );
}

function PermissionGroupMembersBackHeader({
  title,
  permissionGroupId,
  pending,
  action = 'back-to-permission-group-members',
  label = '返回权限组成员',
  onBack
}: {
  readonly title: string;
  readonly permissionGroupId: string;
  readonly pending: boolean;
  readonly action?: string;
  readonly label?: string;
  readonly onBack?: () => void;
}): ReactNode {
  const navigate = useNavigate();
  return (
    <BackHeader
      title={title}
      action={action}
      label={label}
      pending={pending}
      onBack={() => {
        if (onBack) {
          onBack();
          return;
        }
        void navigate({
          to: '/workspace/permission-groups/$permissionGroupId/members',
          params: { permissionGroupId },
          replace: true,
          resetScroll: true
        });
      }}
    />
  );
}

function PermissionGroupPermissionDenied({
  title,
  action,
  label,
  body = '无权管理权限组。',
  onBack
}: {
  readonly title: string;
  readonly action: string;
  readonly label: string;
  readonly body?: string;
  readonly onBack: () => void;
}): ReactNode {
  return (
    <div className="route-outlet">
      <BackHeader title={title} action={action} label={label} onBack={onBack} />
      <section className="panel" role="alert" tabIndex={-1}>
        <p>{body}</p>
      </section>
    </div>
  );
}

function canManagePermissionGroups(appContext: WorkspaceBootstrapContext): boolean {
  return appContext.isCurrentUserAdmin && appContext.currentMembership.role === 'admin';
}

function togglePermissionKey(
  current: readonly PermissionKey[],
  permissionKey: PermissionKey
): readonly PermissionKey[] {
  return current.includes(permissionKey)
    ? current.filter((candidate) => candidate !== permissionKey)
    : [...current, permissionKey];
}

function formatPermissionGroupSummaryMeta(permissionGroup: PermissionGroupSummary): string {
  return `${formatPermissionKeys(permissionGroup.permissionKeys)} · ${permissionGroup.memberCount} 人`;
}

function formatPermissionKeys(permissionKeys: readonly PermissionKey[]): string {
  return formatPermissionKeyLabels(permissionKeys);
}

function formatSelectorMemberGroups(member: MemberSelectorListItem): string {
  return formatMemberGroupNames(member.memberGroupNames);
}
